import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, Upload, X, CheckCircle2, AlertTriangle, Car, FileText,
  Loader2, Plus, Trash2, PenLine, Save, Wrench
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { API } from "../../App";
import { AdjustServicesDialog } from "./AdjustServicesDialog";

// LocalStorage key generator for autosave
const getStorageKey = (bookingId) => `saturn_checkin_${bookingId}`;

const DAMAGE_LOCATIONS = [
  "Front bumper",
  "Rear bumper",
  "Hood/Bonnet",
  "Roof",
  "Driver door",
  "Passenger door",
  "Driver rear door",
  "Passenger rear door",
  "Driver fender",
  "Passenger fender",
  "Driver mirror",
  "Passenger mirror",
  "Windshield",
  "Rear window",
  "Driver side windows",
  "Passenger side windows",
  "Trunk/Boot",
  "Wheels/Rims",
  "Other"
];

const JobCheckInModal = ({ isOpen, onClose, booking, onCheckInComplete, isAdmin }) => {
  const [vehicleReg, setVehicleReg] = useState(
    booking?.vehicle_reg || booking?.vehicle?.reg || ""
  );
  const [notes, setNotes] = useState("");
  const [beforePhotos, setBeforePhotos] = useState([]); // [{file, preview, uploading, photo_id}]
  const [damageRecords, setDamageRecords] = useState([]); // [{description, location, photos}]
  const [showDamageForm, setShowDamageForm] = useState(false);
  const [currentDamage, setCurrentDamage] = useState({ description: "", location: "", photos: [] });
  const [customerSignature, setCustomerSignature] = useState(null);
  const [customerName, setCustomerName] = useState(booking?.customer_name || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasRestoredData, setHasRestoredData] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  // The dialog writes to the DB, so the modal needs the fresh row to keep
  // showing the right scope and price after a change.
  const [liveBooking, setLiveBooking] = useState(booking);
  useEffect(() => { setLiveBooking(booking); }, [booking]);
  
  const beforePhotoInput = useRef(null);
  const damagePhotoInput = useRef(null);
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Autosave to localStorage (debounced)
  const saveToStorage = useCallback(() => {
    if (!booking?.booking_id) return;
    
    const storageKey = getStorageKey(booking.booking_id);
    const dataToSave = {
      vehicleReg,
      notes,
      // Only save uploaded photos (with photo_id), not the file/preview which can't be serialized
      beforePhotos: beforePhotos
        .filter(p => p.photo_id)
        .map(p => ({ photo_id: p.photo_id, preview: p.preview })),
      // Save damage records with their uploaded photo IDs
      damageRecords: damageRecords.map(d => ({
        description: d.description,
        location: d.location,
        photos: d.photos.filter(p => p.photo_id).map(p => ({ photo_id: p.photo_id, preview: p.preview }))
      })),
      customerSignature,
      customerName,
      savedAt: new Date().toISOString()
    };
    
    try {
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    } catch (err) {
      console.warn("Failed to autosave check-in data:", err);
    }
  }, [booking?.booking_id, vehicleReg, notes, beforePhotos, damageRecords, customerSignature, customerName]);

  // Autosave effect - runs when relevant state changes
  useEffect(() => {
    if (!isOpen || !booking?.booking_id || !hasRestoredData) return;
    
    const timeoutId = setTimeout(() => {
      saveToStorage();
    }, 500); // Debounce 500ms
    
    return () => clearTimeout(timeoutId);
  }, [isOpen, hasRestoredData, saveToStorage]);

  // Clear storage on successful submission
  const clearStorage = useCallback(() => {
    if (!booking?.booking_id) return;
    const storageKey = getStorageKey(booking.booking_id);
    try {
      localStorage.removeItem(storageKey);
    } catch (err) {
      console.warn("Failed to clear autosave data:", err);
    }
  }, [booking?.booking_id]);

  // Restore state from localStorage or reset when modal opens
  useEffect(() => {
    if (isOpen && booking) {
      const storageKey = getStorageKey(booking.booking_id);
      let restored = false;
      
      try {
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
          const parsed = JSON.parse(savedData);
          
          // Restore saved data
          if (parsed.vehicleReg) setVehicleReg(parsed.vehicleReg);
          else setVehicleReg(booking?.vehicle_reg || booking?.vehicle?.reg || "");
          
          if (parsed.customerName) setCustomerName(parsed.customerName);
          else setCustomerName(booking?.customer_name || "");
          
          setNotes(parsed.notes || "");
          setBeforePhotos(parsed.beforePhotos || []);
          setDamageRecords(parsed.damageRecords || []);
          setCustomerSignature(parsed.customerSignature || null);
          
          // Notify user that data was restored
          if (parsed.beforePhotos?.length > 0 || parsed.damageRecords?.length > 0 || parsed.notes || parsed.customerSignature) {
            toast.success("Previous check-in data restored", {
              description: "Your unsaved work has been recovered",
              duration: 3000
            });
          }
          restored = true;
        }
      } catch (err) {
        console.warn("Failed to restore autosave data:", err);
      }
      
      // If no saved data, reset to defaults
      if (!restored) {
        setVehicleReg(booking?.vehicle_reg || booking?.vehicle?.reg || "");
        setCustomerName(booking?.customer_name || "");
        setNotes("");
        setBeforePhotos([]);
        setDamageRecords([]);
        setCustomerSignature(null);
      }
      
      setShowDamageForm(false);
      setCurrentDamage({ description: "", location: "", photos: [] });
      setHasRestoredData(true);
    } else if (!isOpen) {
      setHasRestoredData(false);
    }
  }, [isOpen, booking]);

  // Handle before photo selection
  const handleBeforePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      if (file.type && !file.type.startsWith("image/")) {
        // An MPO/HEIC from the phone gallery can arrive with an EMPTY or
        // non-standard MIME type, so a client-side type check silently
        // dropped real photos (Pav, Oct 2026). Only reject what is clearly
        // not an image, and SAY SO. The server validates the real bytes.
        toast.error(`${file.name} is not an image — use the Camera button`);
        continue;
      }

      const preview = URL.createObjectURL(file);
      const photoEntry = { file, preview, uploading: true, photo_id: null };
      setBeforePhotos(prev => [...prev, photoEntry]);

      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const response = await axios.post(
          `${API}/jobs/photos/upload?booking_id=${booking.booking_id}&photo_type=before`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        setBeforePhotos(prev => 
          prev.map(p => p.preview === preview 
            ? { ...p, uploading: false, photo_id: response.data.photo_id }
            : p
          )
        );
        toast.success("Photo uploaded");
      } catch (error) {
        console.error("Photo upload failed:", error);
        setBeforePhotos(prev => prev.filter(p => p.preview !== preview));
        toast.error(error.response?.data?.detail || `Failed to upload ${file.name}`);
      }
    }
    
    if (beforePhotoInput.current) {
      beforePhotoInput.current.value = "";
    }
  };

  // Handle damage photo selection
  const handleDamagePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      if (file.type && !file.type.startsWith("image/")) {
        // An MPO/HEIC from the phone gallery can arrive with an EMPTY or
        // non-standard MIME type, so a client-side type check silently
        // dropped real photos (Pav, Oct 2026). Only reject what is clearly
        // not an image, and SAY SO. The server validates the real bytes.
        toast.error(`${file.name} is not an image — use the Camera button`);
        continue;
      }

      const preview = URL.createObjectURL(file);
      const photoEntry = { file, preview, uploading: true, photo_id: null };
      
      setCurrentDamage(prev => ({
        ...prev,
        photos: [...prev.photos, photoEntry]
      }));

      try {
        const formData = new FormData();
        formData.append("file", file);
        
        const response = await axios.post(
          `${API}/jobs/photos/upload?booking_id=${booking.booking_id}&photo_type=damage`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        setCurrentDamage(prev => ({
          ...prev,
          photos: prev.photos.map(p => p.preview === preview 
            ? { ...p, uploading: false, photo_id: response.data.photo_id }
            : p
          )
        }));
      } catch (error) {
        console.error("Damage photo upload failed:", error);
        setCurrentDamage(prev => ({
          ...prev,
          photos: prev.photos.filter(p => p.preview !== preview)
        }));
        toast.error("Failed to upload damage photo");
      }
    }
    
    if (damagePhotoInput.current) {
      damagePhotoInput.current.value = "";
    }
  };

  // Remove before photo
  const removeBeforePhoto = (preview) => {
    setBeforePhotos(prev => prev.filter(p => p.preview !== preview));
    URL.revokeObjectURL(preview);
  };

  // Add damage record
  const addDamageRecord = () => {
    if (!currentDamage.description.trim() && !currentDamage.location) {
      toast.error("Please describe the damage and select a location");
      return;
    }

    setDamageRecords(prev => [...prev, currentDamage]);
    setCurrentDamage({ description: "", location: "", photos: [] });
    setShowDamageForm(false);
    toast.success("Damage record added");
  };

  // Remove damage record
  const removeDamageRecord = (index) => {
    setDamageRecords(prev => prev.filter((_, i) => i !== index));
  };

  // Signature canvas handlers
  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    setIsDrawing(true);
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoordinates(e, canvas);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoordinates(e, canvas);
    
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#ff6b35";
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setCustomerSignature(null);
  };

  const saveSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL("image/png");
      setCustomerSignature(dataUrl);
      toast.success("Signature captured");
    }
  };

  // Submit check-in
  const handleSubmit = async () => {
    if (!vehicleReg.trim()) {
      toast.error("Please enter the vehicle registration");
      return;
    }

    // Require at least one before photo
    const uploadedPhotos = beforePhotos.filter(p => p.photo_id);
    if (uploadedPhotos.length === 0) {
      toast.error("Please take at least one before photo");
      return;
    }

    // If damage records exist, require signature
    if (damageRecords.length > 0 && !customerSignature) {
      toast.error("Customer signature required for damage acknowledgment");
      return;
    }

    setIsSubmitting(true);
    try {
      const checkInData = {
        vehicle_reg: vehicleReg.trim().toUpperCase(),
        before_photo_ids: uploadedPhotos.map(p => p.photo_id),
        damage_records: damageRecords.map(d => ({
          description: d.description,
          location: d.location,
          photo_ids: d.photos.filter(p => p.photo_id).map(p => p.photo_id)
        })),
        damage_signature: customerSignature,
        customer_name: customerName,
        notes: notes.trim() || null
      };

      const response = await axios.post(
        `${API}/jobs/check-in/${booking.booking_id}`,
        checkInData
      );

      // Clear autosave data on successful submission
      clearStorage();
      
      toast.success("Vehicle checked in successfully!");
      onCheckInComplete?.(response.data);
      onClose();
    } catch (error) {
      console.error("Check-in failed:", error);
      toast.error(error.response?.data?.detail || "Failed to check in");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if there's saved data for this booking
  const hasSavedData = useCallback(() => {
    if (!booking?.booking_id) return false;
    try {
      const storageKey = getStorageKey(booking.booking_id);
      return localStorage.getItem(storageKey) !== null;
    } catch {
      return false;
    }
  }, [booking?.booking_id]);

  // Manual clear saved data
  const handleClearSavedData = () => {
    clearStorage();
    setVehicleReg(booking?.vehicle_reg || booking?.vehicle?.reg || "");
    setCustomerName(booking?.customer_name || "");
    setNotes("");
    setBeforePhotos([]);
    setDamageRecords([]);
    setCustomerSignature(null);
    setShowDamageForm(false);
    setCurrentDamage({ description: "", location: "", photos: [] });
    toast.info("Saved data cleared");
  };

  if (!booking) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Car className="w-6 h-6 text-saturn-orange" />
            Vehicle Check-In
          </DialogTitle>
          <DialogDescription className="text-slate-400 flex items-center justify-between">
            <span>Document vehicle condition before starting work on {booking.customer_name}&apos;s vehicle</span>
            <span className="flex items-center gap-1 text-xs text-green-500">
              <Save className="w-3 h-3" />
              Auto-saving
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Vehicle Info */}
          <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-white flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-400" />
              Vehicle Details
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-400">Customer:</span>{" "}
                <span className="text-white">{booking.customer_name}</span>
              </div>
              <div>
                <span className="text-slate-400">Vehicle:</span>{" "}
                <span className="text-white">
                  {booking.vehicle?.year} {booking.vehicle?.make} {booking.vehicle?.model}
                </span>
              </div>
            </div>
            
            <div className="pt-2">
              <Label className="text-white">Vehicle Registration *</Label>
              <Input
                value={vehicleReg}
                onChange={(e) => setVehicleReg(e.target.value.toUpperCase())}
                placeholder="e.g., 231-D-12345"
                className="bg-slate-800 border-slate-700 text-white uppercase mt-1"
                data-testid="checkin-vehicle-reg"
              />
            </div>

            {/* Scope often changes at the door — this is exactly the
                moment the owner needs it (Amir Ali, Oct 2026). Opens the
                same dialog as the Bookings row and the Job Board. */}
            {isAdmin && !booking.balance_paid && !booking.invoice_id && (
              <div className="pt-2 flex items-center justify-between gap-3 border-t border-slate-700/60 mt-2">
                <p className="text-xs text-slate-400" data-testid="checkin-scope-summary">
                  Booked: {(liveBooking?.services_details || []).length
                    ? (liveBooking.services_details || []).map((s) => s.name).join(", ")
                    : `${(liveBooking?.service_ids || []).length} service(s)`}
                  {" · "}€{Number(liveBooking?.total_price || 0).toFixed(2)}
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-saturn-orange/50 text-saturn-orange hover:bg-saturn-orange/10 shrink-0"
                  onClick={() => setShowAdjust(true)}
                  data-testid="checkin-adjust-services-btn"
                >
                  <Wrench className="w-4 h-4 mr-1" /> Change services / price
                </Button>
              </div>
            )}
          </div>

          {/* Before Photos */}
          <div className="space-y-3">
            <Label className="text-white flex items-center gap-2">
              <Camera className="w-4 h-4 text-saturn-orange" />
              Before Photos *
            </Label>
            <p className="text-slate-500 text-xs">
              Take photos of the vehicle from all angles before starting work
            </p>
            
            <div className="grid grid-cols-3 gap-2">
              {beforePhotos.map((photo, idx) => (
                <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-slate-800">
                  <img 
                    src={photo.preview} 
                    alt={`Before ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {photo.uploading && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-saturn-orange" />
                    </div>
                  )}
                  {photo.photo_id && (
                    <div className="absolute top-1 right-1">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    </div>
                  )}
                  <button
                    onClick={() => removeBeforePhoto(photo.preview)}
                    className="absolute bottom-1 right-1 p-1 bg-red-500/80 rounded hover:bg-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {/* Camera Button - Primary */}
              <label className="aspect-square rounded-lg border-2 border-dashed border-saturn-orange/50 bg-saturn-orange/10 flex flex-col items-center justify-center cursor-pointer hover:border-saturn-orange hover:bg-saturn-orange/20 transition-colors">
                <Camera className="w-8 h-8 text-saturn-orange mb-1" />
                <span className="text-xs text-saturn-orange font-medium">Take Photo</span>
                <input
                  ref={beforePhotoInput}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleBeforePhotoSelect}
                  className="hidden"
                  data-testid="before-photo-camera"
                />
              </label>
              
              {/* Gallery Button - Secondary */}
              <label className="aspect-square rounded-lg border-2 border-dashed border-slate-600 flex flex-col items-center justify-center cursor-pointer hover:border-slate-500 transition-colors">
                <Upload className="w-6 h-6 text-slate-500 mb-1" />
                <span className="text-xs text-slate-500">Gallery</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleBeforePhotoSelect}
                  className="hidden"
                  data-testid="before-photo-gallery"
                />
              </label>
            </div>
          </div>

          {/* Damage Documentation */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-white flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                Existing Damage (Optional)
              </Label>
              {!showDamageForm && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowDamageForm(true)}
                  className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                  data-testid="add-damage-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Damage
                </Button>
              )}
            </div>

            {/* Existing damage records */}
            {damageRecords.length > 0 && (
              <div className="space-y-2">
                {damageRecords.map((damage, idx) => (
                  <div key={idx} className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="text-yellow-400 font-medium">{damage.location}</span>
                        <p className="text-slate-300 text-sm mt-1">{damage.description}</p>
                        {damage.photos.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {damage.photos.map((p, i) => (
                              <img 
                                key={i}
                                src={p.preview}
                                alt={`Damage ${i + 1}`}
                                className="w-12 h-12 object-cover rounded"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeDamageRecord(idx)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add damage form */}
            {showDamageForm && (
              <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                <select
                  value={currentDamage.location}
                  onChange={(e) => setCurrentDamage(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-white"
                  data-testid="damage-location-select"
                >
                  <option value="">Select damage location...</option>
                  {DAMAGE_LOCATIONS.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
                
                <Textarea
                  placeholder="Describe the damage..."
                  value={currentDamage.description}
                  onChange={(e) => setCurrentDamage(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="damage-description"
                />

                <div className="flex gap-2 flex-wrap">
                  {currentDamage.photos.map((photo, idx) => (
                    <div key={idx} className="relative w-16 h-16">
                      <img 
                        src={photo.preview}
                        alt={`Damage photo ${idx + 1}`}
                        className="w-full h-full object-cover rounded"
                      />
                      {photo.uploading && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded">
                          <Loader2 className="w-4 h-4 animate-spin" />
                        </div>
                      )}
                    </div>
                  ))}
                  {/* Camera for damage */}
                  <label className="w-16 h-16 rounded border-2 border-dashed border-yellow-500/50 bg-yellow-500/10 flex flex-col items-center justify-center cursor-pointer hover:border-yellow-500">
                    <Camera className="w-5 h-5 text-yellow-500" />
                    <span className="text-[10px] text-yellow-500">Camera</span>
                    <input
                      ref={damagePhotoInput}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleDamagePhotoSelect}
                      className="hidden"
                      data-testid="damage-photo-camera"
                    />
                  </label>
                  {/* Gallery for damage */}
                  <label className="w-16 h-16 rounded border-2 border-dashed border-slate-600 flex flex-col items-center justify-center cursor-pointer hover:border-slate-500">
                    <Upload className="w-4 h-4 text-slate-500" />
                    <span className="text-[10px] text-slate-500">Gallery</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleDamagePhotoSelect}
                      className="hidden"
                      data-testid="damage-photo-gallery"
                    />
                  </label>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={addDamageRecord}
                    className="bg-yellow-600 hover:bg-yellow-700"
                    data-testid="save-damage-btn"
                  >
                    Save Damage
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowDamageForm(false);
                      setCurrentDamage({ description: "", location: "", photos: [] });
                    }}
                    className="border-slate-600"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Customer Signature (required if damage recorded) */}
          {damageRecords.length > 0 && (
            <div className="space-y-3">
              <Label className="text-white flex items-center gap-2">
                <PenLine className="w-4 h-4 text-saturn-orange" />
                Customer Damage Acknowledgment *
              </Label>
              <p className="text-slate-500 text-xs">
                Customer must sign to acknowledge existing damage before work begins
              </p>

              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Customer full name"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="customer-name-input"
              />

              <div className="bg-slate-800 rounded-lg p-2">
                <div className="relative">
                  <canvas
                    ref={signatureCanvasRef}
                    width={400}
                    height={150}
                    className="w-full bg-white rounded cursor-crosshair touch-none"
                    style={{ touchAction: 'none' }}
                    onMouseDown={startDrawing}
                    onMouseUp={stopDrawing}
                    onMouseMove={draw}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchEnd={stopDrawing}
                    onTouchMove={draw}
                    onTouchCancel={stopDrawing}
                    data-testid="signature-canvas"
                  />
                  <div className="absolute bottom-2 left-2 right-2 border-b border-slate-300 pointer-events-none" />
                  <span className="absolute bottom-3 left-3 text-slate-400 text-xs pointer-events-none">
                    Sign here
                  </span>
                </div>
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={clearSignature}
                    className="border-slate-600"
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveSignature}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="save-signature-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Confirm Signature
                  </Button>
                </div>
                {customerSignature && (
                  <p className="text-green-400 text-sm mt-2 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    Signature captured
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-white">Additional Notes (Optional)</Label>
            <Textarea
              placeholder="Any observations about the vehicle condition..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="checkin-notes"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || beforePhotos.filter(p => p.photo_id).length === 0}
            className="bg-saturn-orange hover:bg-saturn-orange/80"
            data-testid="complete-checkin-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Checking In...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Complete Check-In
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AdjustServicesDialog
        booking={liveBooking}
        open={isAdmin && showAdjust}
        onClose={() => setShowAdjust(false)}
        onSaved={(res) => {
          setShowAdjust(false);
          // The commit response already carries the new scope, so patch
          // it in rather than refetching — there is no single-booking GET
          // on the admin router.
          setLiveBooking((b) => ({
            ...b,
            total_price: res?.new?.total_price ?? b.total_price,
            balance_amount: res?.new?.balance_due ?? b.balance_amount,
            remaining_balance_due: res?.new?.balance_due ?? b.remaining_balance_due,
            duration_minutes: res?.new?.duration_minutes ?? b.duration_minutes,
            services_details: res?.new?.services ?? b.services_details,
          }));
        }}
      />
    </Dialog>
  );
};

export default JobCheckInModal;
